import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, ChevronLeft, ChevronRight, Clock, Plus, Trash2,
  LogOut, Loader2, User, Users, CheckCircle, XCircle, AlertCircle,
  CalendarPlus, Save, Pencil, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

const MANAGER_ROLES = ['owner', 'mgr-sup', 'manager', 'admin'];

/**
 * Schedule Management Page
 * Allows Mgr-Sup and Owner to create/edit schedules and approve time-off requests
 */
export default function ScheduleManagementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState(null);
  const [allStaff, setAllStaff] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('schedule');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [showAddShift, setShowAddShift] = useState(false);
  const [newShift, setNewShift] = useState({
    staff_id: '',
    date: '',
    start_time: '08:00',
    end_time: '16:00',
    position: ''
  });
  
  // Edit shift state
  const [editingShift, setEditingShift] = useState(null);
  const [editShiftData, setEditShiftData] = useState({
    start_time: '',
    end_time: '',
    position: ''
  });
  
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(now.setDate(diff));
  });

  const token = localStorage.getItem('staff_token');

  useEffect(() => {
    if (!token) {
      navigate('/staff/login');
      return;
    }
    fetchStaffInfo();
  }, [token, navigate]);

  useEffect(() => {
    if (staffInfo && MANAGER_ROLES.includes(staffInfo.role?.toLowerCase())) {
      fetchAllStaff();
      fetchSchedules();
      fetchTimeOffRequests();
    }
  }, [staffInfo, currentWeekStart]);

  const fetchStaffInfo = async () => {
    try {
      const res = await api.get(`/staff-portal/profile?token=${token}`);
      setStaffInfo(res.data);
      if (!MANAGER_ROLES.includes(res.data.role?.toLowerCase())) {
        toast.error('Access denied. Managers only.');
        navigate('/staff/profile');
      }
    } catch (err) {
      console.error('Failed to fetch staff info:', err);
      navigate('/staff/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStaff = async () => {
    try {
      const res = await api.get(`/staff-portal/all-staff?token=${token}`);
      setAllStaff(res.data.staff || []);
    } catch (err) {
      console.error('Failed to fetch staff:', err);
    }
  };

  const fetchSchedules = async () => {
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const res = await api.get(
        `/staff-portal/all-schedules?token=${token}&start_date=${currentWeekStart.toISOString().split('T')[0]}&end_date=${weekEnd.toISOString().split('T')[0]}`
      );
      setSchedules(res.data.schedules || []);
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    }
  };

  const fetchTimeOffRequests = async () => {
    try {
      const res = await api.get(`/staff-portal/pending-time-off?token=${token}`);
      setTimeOffRequests(res.data.requests || []);
    } catch (err) {
      console.error('Failed to fetch time off requests:', err);
    }
  };

  const handleCreateShift = async () => {
    if (!newShift.staff_id || !newShift.date || !newShift.start_time || !newShift.end_time) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const res = await api.post(`/staff-portal/create-shift?token=${token}`, newShift);
      toast.success(res.data.message);
      setShowAddShift(false);
      setNewShift({ staff_id: '', date: '', start_time: '08:00', end_time: '16:00', position: '' });
      fetchSchedules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create shift');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!confirm('Delete this shift?')) return;
    
    try {
      await api.delete(`/staff-portal/delete-shift/${shiftId}?token=${token}`);
      toast.success('Shift deleted');
      fetchSchedules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete shift');
    }
  };

  const handleOpenEditShift = (shift) => {
    setEditingShift(shift);
    setEditShiftData({
      start_time: shift.start_time,
      end_time: shift.end_time,
      position: shift.position || ''
    });
  };

  const handleCloseEditShift = () => {
    setEditingShift(null);
    setEditShiftData({ start_time: '', end_time: '', position: '' });
  };

  const handleEditShift = async () => {
    if (!editingShift) return;
    
    if (!editShiftData.start_time || !editShiftData.end_time) {
      toast.error('Please fill start and end times');
      return;
    }
    
    try {
      const res = await api.put(`/staff-portal/edit-shift/${editingShift.id}?token=${token}`, {
        start_time: editShiftData.start_time,
        end_time: editShiftData.end_time,
        position: editShiftData.position || null
      });
      toast.success(res.data.message || 'Shift updated');
      handleCloseEditShift();
      fetchSchedules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update shift');
    }
  };

  const handleTimeOffAction = async (requestId, action) => {
    try {
      await api.post(`/staff-portal/time-off-action/${requestId}?token=${token}`, { action });
      toast.success(`Request ${action}d`);
      fetchTimeOffRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update request');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_info');
    toast.success('Logged out');
    navigate('/staff/login');
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getShiftsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return schedules.filter(s => s.date === dateStr);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const weekDays = getWeekDays();
  const weekEndDate = new Date(currentWeekStart);
  weekEndDate.setDate(weekEndDate.getDate() + 6);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/staff/profile" className="text-gray-500 hover:text-gray-700 dark:text-gray-400">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-bold text-gray-900 dark:text-white">Schedule Management</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{staffInfo?.name} • {staffInfo?.role}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('schedule')}
              className={`py-3 px-4 font-medium border-b-2 transition-colors ${
                activeTab === 'schedule'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Schedule
            </button>
            <button
              onClick={() => setActiveTab('timeoff')}
              className={`py-3 px-4 font-medium border-b-2 transition-colors relative ${
                activeTab === 'timeoff'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              Time Off Requests
              {timeOffRequests.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {timeOffRequests.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {activeTab === 'schedule' && (
          <>
            {/* Week Navigation & Add Shift */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="text-center min-w-[200px]">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button onClick={() => setShowAddShift(true)} className="bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Shift
              </Button>
            </div>

            {/* Add Shift Form */}
            {showAddShift && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Create New Shift</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Staff Member</label>
                    <select
                      value={newShift.staff_id}
                      onChange={(e) => setNewShift({ ...newShift, staff_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select staff...</option>
                      {allStaff.map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                    <Input
                      type="date"
                      value={newShift.date}
                      onChange={(e) => setNewShift({ ...newShift, date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
                    <Input
                      type="time"
                      value={newShift.start_time}
                      onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
                    <Input
                      type="time"
                      value={newShift.end_time}
                      onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Position</label>
                    <Input
                      placeholder="e.g., Barista"
                      value={newShift.position}
                      onChange={(e) => setNewShift({ ...newShift, position: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleCreateShift} className="bg-teal-600 hover:bg-teal-700">
                    <Save className="w-4 h-4 mr-2" />
                    Save Shift
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddShift(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Edit Shift Modal */}
            {editingShift && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleCloseEditShift}>
                <div 
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 w-full max-w-md mx-4 shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-lg">Edit Shift</h3>
                    <button 
                      onClick={handleCloseEditShift}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Shift Info Display */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
                    <p className="font-medium text-gray-900 dark:text-white">{editingShift.staff_name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(editingShift.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
                        <Input
                          type="time"
                          value={editShiftData.start_time}
                          onChange={(e) => setEditShiftData({ ...editShiftData, start_time: e.target.value })}
                          data-testid="edit-shift-start-time"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
                        <Input
                          type="time"
                          value={editShiftData.end_time}
                          onChange={(e) => setEditShiftData({ ...editShiftData, end_time: e.target.value })}
                          data-testid="edit-shift-end-time"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Position</label>
                      <Input
                        placeholder="e.g., Barista"
                        value={editShiftData.position}
                        onChange={(e) => setEditShiftData({ ...editShiftData, position: e.target.value })}
                        data-testid="edit-shift-position"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-6">
                    <Button 
                      onClick={handleEditShift} 
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                      data-testid="save-edit-shift"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={handleCloseEditShift}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Grid */}
            <div className="space-y-4">
              {weekDays.map((day, index) => {
                const shifts = getShiftsForDay(day);
                const today = isToday(day);
                
                return (
                  <div 
                    key={index}
                    className={`bg-white dark:bg-gray-800 rounded-xl border ${today ? 'border-teal-500 ring-2 ring-teal-500/20' : 'border-gray-200 dark:border-gray-700'} p-4`}
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className={`w-14 h-14 rounded-lg flex flex-col items-center justify-center ${today ? 'bg-teal-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                        <span className="text-xs font-medium">{day.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                        <span className="text-xl font-bold">{day.getDate()}</span>
                      </div>
                      <div>
                        <p className={`font-semibold ${today ? 'text-teal-600' : 'text-gray-900 dark:text-white'}`}>
                          {day.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          {today && <span className="ml-2 text-xs bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 px-2 py-0.5 rounded-full">Today</span>}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{shifts.length} shift{shifts.length !== 1 ? 's' : ''} scheduled</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto"
                        onClick={() => {
                          setNewShift({ ...newShift, date: day.toISOString().split('T')[0] });
                          setShowAddShift(true);
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {shifts.length > 0 ? (
                      <div className="grid gap-2">
                        {shifts.map((shift) => (
                          <div key={shift.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <User className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{shift.staff_name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                                  {shift.position && <span className="ml-2 text-teal-600">• {shift.position}</span>}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{shift.hours}h</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                onClick={() => handleOpenEditShift(shift)}
                                data-testid={`edit-shift-${shift.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteShift(shift.id)}
                                data-testid={`delete-shift-${shift.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-2">No shifts scheduled</p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {activeTab === 'timeoff' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pending Time Off Requests</h2>
            
            {timeOffRequests.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">No pending requests</p>
              </div>
            ) : (
              timeOffRequests.map((request) => (
                <div key={request.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{request.staff_name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {request.request_type?.toUpperCase() || 'PTO'} • {request.start_date} to {request.end_date}
                      </p>
                      {request.reason && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          Reason: {request.reason}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Submitted: {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleTimeOffAction(request.id, 'approve')}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => handleTimeOffAction(request.id, 'deny')}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Deny
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
